export type BridgeStatus =
  | { connected: false }
  | {
      connected: true;
      mt5_login: number | null;
      mt5_server: string | null;
      bridge_version: string | null;
    };

/** GET /health. `status` is liveness (always "ok" if the process
 *  answers); `ready` means every component is usable, which is what the
 *  Overview strip reports. */
export interface HealthResponse {
  status: string;
  ready: boolean;
  components: {
    db: { ok: boolean; error?: string };
    bridge: {
      ok: boolean;
      connected: boolean;
      mt5_login?: number | null;
      mt5_server?: string | null;
      bridge_version?: string | null;
    };
    scheduler: {
      ok: boolean;
      running: boolean;
      jobs?: { id: string; next_run: string | null }[];
      error?: string;
    };
    keystore: { ok: boolean };
  };
}

export interface AccountState {
  login: number;
  server: string;
  name: string;
  currency: string;
  balance: number;
  equity: number;
  margin_free: number;
  leverage: number;
  trade_allowed: boolean;
  trade_expert: boolean;
  open_positions: number;
}

export interface Position {
  ticket: number;
  symbol: string;
  side: "buy" | "sell";
  volume: number;
  price_open: number;
  sl: number;
  tp: number;
  profit: number;
  swap: number;
  comment: string;
}

export interface Trade {
  id: number;
  correlation_id: string;
  signal_event_id: number | null;
  symbol: string;
  direction: string;
  status: "filled" | "rejected" | "skipped" | "pending" | "closed";
  requested_lot: number;
  requested_sl: number | null;
  requested_tp: number | null;
  mt5_ticket: number | null;
  filled_lot: number | null;
  fill_price: number | null;
  entry_time: string | null;
  exit_time: string | null;
  exit_price: number | null;
  pnl_currency: number | null;
  error: string | null;
  created_at: string | null;
}

function authHeaders(): Record<string, string> {
  // Centralised: every request includes the Bearer token when one is stored.
  // Pre-auth (no token) the header is simply omitted; the backend returns 401
  // for protected routes and 200 for open ones (/auth/status, /health).
  const t = getStoredToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Custom event fired when a request returns 401 while a token IS stored —
 *  i.e. the token has expired or been revoked server-side mid-session. The
 *  AuthContext listens for this and bounces the user back to /login with
 *  the current route preserved in state.from. Pre-auth 401s (token never
 *  set) are not interesting — those are the legitimate "you need to log in"
 *  responses on the login page itself. */
export const AUTH_EXPIRED_EVENT = "fxdash.auth.expired";

function maybeNotify401(status: number): void {
  if (status !== 401) return;
  if (getStoredToken() == null) return;
  try {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  } catch {
    // No window (SSR / very old jsdom). Silent — the next reload will
    // pick up the cleared token via the AuthContext refresh.
  }
}

async function getJSON<T>(url: string): Promise<T | { error: string; status: number }> {
  try {
    const r = await fetch(url, {
      headers: { Accept: "application/json", ...authHeaders() },
    });
    if (!r.ok) {
      maybeNotify401(r.status);
      let detail = `HTTP ${r.status}`;
      try {
        const body = await r.json();
        if (body?.detail) detail = body.detail;
      } catch {
        // ignore
      }
      return { error: detail, status: r.status };
    }
    return (await r.json()) as T;
  } catch (err) {
    return { error: String(err), status: 0 };
  }
}

export function isError(
  v: unknown,
): v is { error: string; status: number } {
  // A transport/HTTP error is {error: string, status: number}. Domain
  // payloads can legitimately carry both keys too (a backtest job has
  // status: "completed" and error: null), so key presence alone is not
  // enough - the types must match the error shape.
  if (typeof v !== "object" || v === null) return false;
  const o = v as { error?: unknown; status?: unknown };
  return typeof o.error === "string" && typeof o.status === "number";
}

export interface RiskState {
  kill_switch: {
    active: boolean;
    reason: string | null;
    set_at: string | null;
  };
  limits: {
    risk_per_trade_pct: number;
    daily_loss_limit_pct: number;
    max_open_positions_total: number;
    max_open_positions_per_symbol: number;
    min_lot: number;
    max_lot: number;
  };
  today: {
    balance_date: string;
    starting_equity: number;
    starting_balance: number;
    realised_pnl: number;
    closed_count: number;
  } | null;
}

export interface MatrixCell {
  strategy_id: string;
  action: "buy" | "sell" | "close" | "veto" | null;
  age_seconds: number | null;
  price: number | null;
  // False when the strategy was validated on another symbol: its vote
  // here neither completes nor blocks a convergence (F61). Absent from
  // older backends, which means applicable.
  applicable?: boolean;
}

export interface MatrixSymbol {
  symbol: string;
  strategies: MatrixCell[];
}

export interface ConvergenceMatrix {
  mode: "single" | "strict" | "boost";
  window_seconds: number;
  enabled_strategies: string[];
  symbols: MatrixSymbol[];
  // Symbols where only one strategy applies, so "strict" is really one
  // strategy's say-so (F61). Absent from older backends.
  warnings?: string[];
}

export interface CloseAllResult {
  requested: number;
  closed: number[];
  failed: { ticket: number; error: string }[];
}

export type NewsImportance = "high" | "medium" | "low" | "holiday";

export interface NewsEvent {
  id: number;
  currency: string;
  country: string | null;
  name: string;
  importance: NewsImportance;
  scheduled_time: string;
  actual_value: string | null;
  forecast_value: string | null;
  previous_value: string | null;
  source: string;
}

export interface JournalEntryT {
  id: number;
  trade_id: number | null;
  mt5_ticket: number | null;
  symbol: string;
  direction: string;
  lot_size: number;
  entry_time: string | null;
  entry_price: number | null;
  exit_time: string | null;
  exit_price: number | null;
  pnl_currency: number | null;
  contributing_strategies: string[];
  safe_to_trade_status: string | null;
  equity_at_entry: number | null;
  tags: string[];
  notes: string;
  entry_snapshot_url: string | null;
  // strategy id -> why it signalled (strategy, params, reason, indicators).
  entry_context?: Record<string, EntryContext>;
  created_at: string | null;
  updated_at: string | null;
}

export interface EntryContext {
  strategy?: string;
  params?: Record<string, number>;
  reason?: string;
  indicators?: Record<string, number | null>;
}

export interface QuarterlyReviewRow {
  strategy: string;
  trades: number;
  net_pnl: number;
  profit_factor: number | null;
  local: boolean;
  status: "keep" | "retire" | "too_few_trades";
  action?: string;
}

export interface QuarterlyReview {
  enabled?: boolean;
  generated_at: string;
  min_trades: number;
  retire_below_profit_factor: number;
  applied: boolean;
  strategies: QuarterlyReviewRow[];
}

export interface NewsStatus {
  symbol: string;
  as_of: string;
  status: "GREEN" | "AMBER" | "RED";
  reason: string | null;
  triggers: {
    currency: string;
    name: string;
    importance: NewsImportance;
    scheduled_time: string;
  }[];
}

async function postJSON<T>(
  url: string,
  body?: unknown,
): Promise<T | { error: string; status: number }> {
  try {
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!r.ok) {
      maybeNotify401(r.status);
      let detail = `HTTP ${r.status}`;
      try {
        const b = await r.json();
        if (b?.detail) detail = b.detail;
      } catch {
        // ignore
      }
      return { error: detail, status: r.status };
    }
    return (await r.json()) as T;
  } catch (err) {
    return { error: String(err), status: 0 };
  }
}

async function deleteJSON<T>(url: string): Promise<T | { error: string; status: number }> {
  try {
    const r = await fetch(url, { method: "DELETE", headers: authHeaders() });
    if (!r.ok) {
      maybeNotify401(r.status);
      let detail = `HTTP ${r.status}`;
      try {
        const b = await r.json();
        if (b?.detail) detail = b.detail;
      } catch {
        // ignore
      }
      return { error: detail, status: r.status };
    }
    return (await r.json()) as T;
  } catch (err) {
    return { error: String(err), status: 0 };
  }
}

async function putJSON<T>(
  url: string,
  body: unknown,
): Promise<T | { error: string; status: number }> {
  try {
    const r = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      maybeNotify401(r.status);
      let detail = `HTTP ${r.status}`;
      try {
        const b = await r.json();
        if (b?.detail) detail = b.detail;
      } catch {
        // ignore
      }
      return { error: detail, status: r.status };
    }
    return (await r.json()) as T;
  } catch (err) {
    return { error: String(err), status: 0 };
  }
}

/** {iat, exp} (epoch seconds) read from the stored token's payload, or
 *  null when there is no token or it is not a JWT. Purely informational -
 *  the backend is the authority; this only drives renewal timing. */
export function tokenLifetime(): { iat: number; exp: number } | null {
  const token = getStoredToken();
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { iat?: number; exp?: number };
    if (typeof claims.iat !== "number" || typeof claims.exp !== "number") return null;
    return { iat: claims.iat, exp: claims.exp };
  } catch {
    return null;
  }
}

export interface AuthStatus {
  key_fingerprint?: string | null;
  enabled: boolean;
  jwt_configured: boolean;
  user_count: number;
  current_user: { id: number; email: string; created_at: string | null } | null;
}

// Transition Plan lever L15 — Trading Opportunity Intelligence (slice 1).
export interface IntelligenceBucket {
  bucket_key: string;
  trade_count: number;
  win_count: number;
  win_rate: number;          // 0..1
  avg_pnl: number;
  total_pnl: number;
  expectancy: number;
  sample_sufficient: boolean;
  threshold: number;
}

export interface IntelligencePair {
  symbol: string;
  trade_count: number;
  win_count: number;
  win_rate: number;
  avg_pnl: number;
  total_pnl: number;
  expectancy: number;
  profit_factor: number | null;  // null when math.inf (no losers)
  sample_sufficient: boolean;
  threshold: number;
}

export interface IntelligenceCorrelation {
  symbol_a: string;
  symbol_b: string;
  correlation: number | null;    // null when insufficient overlap
  overlap_days: number;
}

export interface IntelligenceSummary {
  trades_total: number;
  closed_trades: number;
  days_of_history: number;
  earliest_exit: string | null;
  latest_exit: string | null;
  threshold_trades: number;
  threshold_days: number;
  trigger_fired: boolean;
}

export interface IntelligenceOutlook {
  now: string;
  current_day_key: string;
  current_hour_key: string;
  enough_data: boolean;
  current_day: IntelligenceBucket | null;
  current_hour: IntelligenceBucket | null;
  best_day: IntelligenceBucket | null;
  worst_day: IntelligenceBucket | null;
  best_hour: IntelligenceBucket | null;
  worst_hour: IntelligenceBucket | null;
  best_pair: IntelligencePair | null;
  worst_pair: IntelligencePair | null;
}

export interface ExpectancyCurvePoint {
  index: number;
  exit_time: string;
  rolling: number;
  cumulative: number;
}

export interface ExpectancyCurve {
  window: number;
  trade_count: number;
  points: ExpectancyCurvePoint[];
}

export interface IntelligencePayload {
  summary: IntelligenceSummary;
  day_of_week: IntelligenceBucket[];
  hour_of_day: IntelligenceBucket[];
  pair_scoreboard: IntelligencePair[];
  correlation: IntelligenceCorrelation[];
  outlook?: IntelligenceOutlook;
  source?: "live" | "backtest";
  job_id?: number | null;
}

// Transition Plan lever L14 slice 2 — backtest engine API.
export interface BacktestParamSpec {
  kind: "int" | "float";
  default: number;
  min: number;
  max: number;
  label: string;
}

export interface BacktestStrategySpec {
  name: string;
  label: string;
  description: string;
  params: Record<string, BacktestParamSpec>;
}

export interface BacktestMetrics {
  trade_count: number;
  win_count: number;
  loss_count: number;
  win_rate: number;
  avg_win: number;
  avg_loss: number;
  expectancy: number;
  profit_factor: number | null;
  gross_profit: number;
  gross_loss: number;
  total_pnl: number;
  return_pct: number;
  max_drawdown_abs: number;
  max_drawdown_pct: number;
  sharpe_annualised: number;
  // Added with backtest parity (G1). Optional: jobs stored before it
  // carry none of these.
  expectancy_r?: number | null;
  trades_per_year?: number;
  r_positions?: number;
  per_year?: Record<
    string,
    {
      positions: number;
      total_pnl: number | null;
      win_rate: number | null;
      profit_factor: number | null;
      expectancy_r: number | null;
    }
  >;
  parity?: {
    enabled: boolean;
    rejections: Record<string, number>;
    events: Record<string, number>;
  };
  realism?: RealismReport;
}

/** What a run's realistic-cost options and M1 exit resolution used. */
export interface RealismReport {
  news_events?: number;
  news_warning?: string;
  intrabar?: {
    m1_bars: number;
    note?: string;
    ambiguous_bars_resolved_tp?: number;
    ambiguous_bars_resolved_sl?: number;
    ambiguous_bars_no_m1?: number;
  };
  costs?: Record<string, number | boolean>;
}

/** `true` = replay the live risk gate, sizer and exits; an object turns
 * individual behaviours off; absent/false = fixed-lot mechanical replay. */
export type BacktestParity = boolean | Partial<Record<
  "risk_gate" | "size_by_risk" | "live_exits" | "daily_loss_policy" | "weekend_flatten",
  boolean
>>;

export interface BacktestEquityPoint {
  ts_us: number;
  equity: number;
}

export interface BacktestTradeRow {
  direction: string;
  entry_ts_us: number;
  entry_price: number;
  exit_ts_us: number;
  exit_price: number;
  pnl_currency: number;
  pnl_pips: number;
  exit_reason: string;
  bars_held: number;
}

/** A date range of the stored price history (G11), used instead of a CSV
 * upload. symbol/timeframe default to the run's own. */
export interface StoreRange {
  start: string;
  end: string;
  symbol?: string;
  timeframe?: string;
  offset_minutes?: number;
}
export interface WithStore {
  store?: StoreRange;
}

export interface MarketDataSeries {
  symbol: string;
  timeframe: string;
  bars: number;
  first: string | null;
  last: string | null;
  excluded?: number;
}
export interface MarketDataQuality {
  symbol: string;
  timeframe: string;
  would_flag: number;
  by_reason: Record<string, number>;
  already_excluded: number;
  examples: { time: string; reason: string; detail: Record<string, unknown> }[];
}

export interface MarketDataGaps {
  symbol: string;
  timeframe: string;
  gaps: number;
  missing_bars_estimate: number;
  largest: { from: string; to: string; missing_bars: number }[];
}
export interface MarketDataFill {
  id: string;
  symbol: string;
  timeframe: string;
  start: string;
  end: string;
  status: "running" | "completed" | "failed";
  chunks_total: number;
  chunks_done: number;
  fetched: number;
  inserted: number;
  empty_chunks: number;
  error: string | null;
  started_at: string;
  finished_at: string | null;
}
export interface MarketDataImportResult {
  symbol: string;
  timeframe: string;
  read: number;
  inserted: number;
  already_stored: number;
  first: string | null;
  last: string | null;
}

export interface BacktestJobSummary {
  id: number;
  status: "pending" | "running" | "completed" | "failed";
  symbol: string;
  timeframe: string;
  strategy_name: string;
  bars_count: number;
  source?: string;
  source_label: string | null;
  ending_balance: number | null;
  total_pnl: number | null;
  created_at: string | null;
  completed_at: string | null;
  error: string | null;
}

export interface BacktestJobDetail extends BacktestJobSummary {
  strategy_params: Record<string, number>;
  fill_params: Record<string, number | BacktestParity>;
  lot_size: number;
  starting_balance: number;
  metrics: BacktestMetrics | null;
  equity_curve: BacktestEquityPoint[];
  started_at: string | null;
  trades: BacktestTradeRow[];
}

export interface BacktestSingleConfig {
  mode?: "single";
  symbol: string;
  timeframe: string;
  strategy: string;
  params: Record<string, number>;
  fill: Record<string, number>;
  lot_size: number;
  starting_balance: number;
  parity?: BacktestParity;
}

export interface BacktestConvergenceConfig {
  mode: "convergence";
  symbol: string;
  timeframe: string;
  window_seconds: number;
  /** 0 = strict; > 1 = agreement boosts size (needs parity). */
  boost_mult?: number;
  sl_pips: number;
  tp_pips: number;
  strategies: ConvergenceLeg[];
  fill: Record<string, number>;
  lot_size: number;
  starting_balance: number;
  parity?: BacktestParity;
}

/** A convergence leg. `timeframe` (optional, higher than the run's) feeds
 * the leg bars of that timeframe built from the run's bars. */
export interface ConvergenceLeg {
  name: string;
  params: Record<string, number>;
  timeframe?: string;
}

export interface PortfolioResult {
  job_ids: number[];
  legs: {
    label: string;
    trades: number;
    total_pnl: number;
    profit_factor: number | null;
    max_drawdown_abs: number;
    max_drawdown_pct: number;
  }[];
  combined: {
    trades: number;
    total_pnl: number;
    return_pct: number | null;
    max_drawdown_abs: number;
    max_drawdown_pct: number;
    drawdown_vs_sum_of_legs: number | null;
  };
  months: number;
  correlation: {
    labels: string[];
    matrix: (number | null)[][];
    flag_above: number;
    flagged: { a: string; b: string; correlation: number }[];
  };
  limitations: string;
}

export interface StrategyValidationStatus {
  strategy: string;
  validation: { id: number; verdict: "pass" | "archived" | "retired"; created_at: string } | null;
  breaker: {
    tripped: boolean;
    reason: string | null;
    trades: number;
    drawdown_pct: number;
    losing_streak: number;
    drawdown_limit_pct: number | null;
    streak_limit: number | null;
  } | null;
}

export interface LiveParityRow {
  strategy: string;
  symbol?: string;
  timeframe?: string;
  error?: string;
  expected?: number;
  received?: number;
  matched?: number;
  match_pct?: number | null;
  missing?: { bar_close: string; action: string }[];
  extra?: { bar_close: string; action: string }[];
  fills?: number;
  avg_slippage_pips?: number | null;
  max_slippage_pips?: number | null;
  refused_by_rule?: Record<string, number>;
  // Weekly report only.
  assumed_slippage_pips?: number | null;
  problems?: string[];
}

export interface WeeklyParityReport {
  generated_at: string;
  days: number;
  ok: boolean;
  subject: string;
  notified?: boolean;
  strategies: LiveParityRow[];
}

export interface WeeklyParityStatus {
  enabled: boolean;
  weekday: number;
  hour_utc: number;
  last: WeeklyParityReport | null;
}

export type BacktestRunConfig = BacktestSingleConfig | BacktestConvergenceConfig;

export interface BacktestSweepRow {
  params: Record<string, number>;
  ending_balance: number;
  metrics: {
    trade_count: number;
    win_rate: number;
    expectancy: number;
    profit_factor: number | null;
    total_pnl: number;
    return_pct: number;
    max_drawdown_pct: number;
    sharpe_annualised: number;
  };
}

export interface BacktestSweepResult {
  strategy: string;
  rank_by: string;
  combinations: number;
  results: BacktestSweepRow[];
}

export interface BacktestSweepConfig {
  strategy: string;
  grid: Record<string, number[]>;
  rank_by: string;
  fill: Record<string, number>;
  lot_size: number;
  starting_balance: number;
  parity?: BacktestParity;
}

interface WfaMetricsBrief {
  trade_count: number;
  win_rate: number;
  expectancy: number;
  profit_factor: number | null;
  total_pnl: number;
  max_drawdown_pct: number;
}

export interface WalkForwardFold {
  fold_index: number;
  is_range: [number, number];
  oos_range: [number, number];
  best_params: Record<string, number>;
  is_expectancy: number;
  oos: WfaMetricsBrief;
}

export interface WalkForwardResult {
  rank_by: string;
  is_bars: number;
  oos_bars: number;
  n_folds: number;
  efficiency: number | null;
  combined_oos: WfaMetricsBrief;
  folds: WalkForwardFold[];
}

export interface ValidationCriterion {
  name: string;
  value: number | null;
  threshold: number;
  passed: boolean;
  detail: string;
}

export interface ValidationConfig {
  strategy: string;
  symbol: string;
  timeframe: string;
  grid: Record<string, number[]>;
  is_bars: number;
  oos_bars: number;
  rank_by: string;
  fill: Record<string, number>;
  starting_balance: number;
  parity?: BacktestParity;
  criteria?: Record<string, number>;
}

export interface ValidationResult {
  id: number;
  strategy: string;
  params: Record<string, number>;
  symbol: string;
  timeframe: string;
  verdict: "pass" | "archived" | "retired";
  criteria: ValidationCriterion[];
  bars_count: number;
  created_at: string | null;
  report?: {
    split: { bars: number; development_bars: number; holdout_bars: number };
    sweep: { combinations: number; peak_params: Record<string, number> };
    monte_carlo: Record<string, number>;
    news_coverage: { events: number; covered_fraction: number } | null;
    realism?: RealismReport;
    // Basket validations (POST /backtest/validate-basket) only.
    basket?: string[];
    per_pair?: Record<string, { trade_count: number; total_pnl: number; profit_factor: number | null }>;
    holdout_per_pair?: Record<string, number>;
    warnings?: string[];
    correlation?: { flagged: { a: string; b: string; correlation: number }[] } | null;
  };
  pair_validation_ids?: Record<string, number>;
}

export interface BasketValidationConfig {
  strategy: string;
  timeframe: string;
  symbols: string[];
  store: { start: string; end: string };
  grid: Record<string, number[]>;
  is_days: number;
  oos_days: number;
  rank_by: string;
  fill: Record<string, number>;
  starting_balance: number;
  parity: boolean;
  criteria: Record<string, number>;
}

export interface WalkForwardConfig {
  strategy: string;
  grid: Record<string, number[]>;
  is_bars: number;
  oos_bars: number;
  rank_by: string;
  fill: Record<string, number>;
  lot_size: number;
  starting_balance: number;
  parity?: BacktestParity;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_minutes: number;
}

const AUTH_TOKEN_KEY = "fxdash.auth.token";

export function getStoredToken(): string | null {
  try {
    return window.localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string): void {
  try {
    window.localStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch {
    // ignore
  }
}

export function clearToken(): void {
  try {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // ignore
  }
}

async function authedGet<T>(url: string): Promise<T | { error: string; status: number }> {
  const token = getStoredToken();
  if (!token) return { error: "no token", status: 401 };
  try {
    const r = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    if (!r.ok) {
      maybeNotify401(r.status);
      let detail = `HTTP ${r.status}`;
      try {
        const body = await r.json();
        if (body?.detail) detail = body.detail;
      } catch {
        // ignore
      }
      return { error: detail, status: r.status };
    }
    return (await r.json()) as T;
  } catch (err) {
    return { error: String(err), status: 0 };
  }
}

export const api = {
  bridgeStatus: () => getJSON<BridgeStatus>("/bridge/status"),
  health: () => getJSON<HealthResponse>("/health"),
  accountState: () => getJSON<AccountState>("/account/state"),
  accountPositions: () => getJSON<{ positions: Position[] }>("/account/positions"),
  trades: (limit = 25) => getJSON<{ trades: Trade[] }>(`/trades?limit=${limit}`),
  riskState: () => getJSON<RiskState>("/risk/state"),
  convergenceMatrix: () => getJSON<ConvergenceMatrix>("/convergence/matrix"),
  // Favourite strategies (Backtest > Favourites). Saved from a completed
  // single run; the convergence picker draws from this list.
  localSignals: () => getJSON<LocalSignalStatus>("/signals/local"),
  localSignalsRunNow: () =>
    postJSON<{ results: LocalSignalResult[] }>("/signals/local/run"),
  backtestSymbols: () =>
    getJSON<{
      symbols: BrokerSymbol[];
      source: "broker" | "fallback";
      market_watch_only: boolean;
    }>("/backtest/symbols"),
  backtestSymbolSpec: (symbol: string) =>
    getJSON<{ spec: InstrumentSpec }>(
      `/backtest/symbol-spec?symbol=${encodeURIComponent(symbol)}`,
    ),
  backtestFavourites: () => getJSON<{ favourites: FavouriteStrategy[] }>("/backtest/favourites"),
  backtestSaveFavourite: (jobId: number, strategy_id: string, note?: string) =>
    postJSON<{ favourite: FavouriteStrategy }>(`/backtest/${jobId}/favourite`, {
      strategy_id,
      ...(note ? { note } : {}),
    }),
  backtestDeleteFavourite: (id: number) =>
    deleteJSON<{ ok: true; favourites: FavouriteStrategy[] }>(`/backtest/favourites/${id}`),
  strategyCatalogue: () =>
    getJSON<{
      strategies: StrategyCatalogueEntry[];
      enabled: string[];
      recommended_window: WindowRecommendation | null;
      current_window: number;
    }>("/convergence/strategies"),
  killSwitch: (reason: string) =>
    postJSON<{ kill_switch: RiskState["kill_switch"] }>("/risk/kill", { reason }),
  armRisk: () => postJSON<{ kill_switch: RiskState["kill_switch"] }>("/risk/arm"),
  closeAll: () => postJSON<CloseAllResult>("/trades/close-all"),
  newsStatus: (symbol: string) =>
    getJSON<NewsStatus>(`/news/status?symbol=${encodeURIComponent(symbol)}`),
  newsEvents: (hoursForward = 24) =>
    getJSON<{ as_of: string; events: NewsEvent[] }>(
      `/news/events?hours_back=2&hours_forward=${hoursForward}`,
    ),
  newsRefresh: () => postJSON<{ written: number }>("/news/refresh"),
  backtestPortfolio: (jobIds: number[]) =>
    postJSON<PortfolioResult>("/backtest/portfolio", { job_ids: jobIds }),
  signalsValidationStatus: () =>
    getJSON<{ strategies: StrategyValidationStatus[] }>("/signals/validation-status"),
  quarterlyReview: () => getJSON<QuarterlyReview>("/signals/quarterly-review"),
  signalsParityWeekly: () => getJSON<WeeklyParityStatus>("/signals/parity-report/weekly"),
  signalsParityWeeklyRun: () =>
    postJSON<WeeklyParityReport>("/signals/parity-report/weekly/run"),
  signalsParityReport: (days = 7) =>
    getJSON<{ days: number; strategies: LiveParityRow[] }>(`/signals/parity-report?days=${days}`),
  newsImport: async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch("/news/import", { method: "POST", headers: authHeaders(), body: fd });
      const body = await r.json().catch(() => null);
      if (!r.ok) {
        maybeNotify401(r.status);
        return { error: String(body?.detail ?? `HTTP ${r.status}`), status: r.status };
      }
      return body as {
        inserted: number;
        already_present: number;
        rejected: number;
        problems: string[];
      };
    } catch (err) {
      return { error: String(err), status: 0 };
    }
  },
  journalList: (params: { symbol?: string; tag?: string; search?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.symbol) q.set("symbol", params.symbol);
    if (params.tag) q.set("tag", params.tag);
    if (params.search) q.set("search", params.search);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return getJSON<{ entries: JournalEntryT[] }>(`/journal${qs ? `?${qs}` : ""}`);
  },
  journalPatch: (
    id: number,
    body: { notes?: string; tags?: string; entry_snapshot_url?: string },
  ) =>
    fetch(`/journal/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(body),
    }).then((r) => {
      if (!r.ok) maybeNotify401(r.status);
      return r.ok ? (r.json() as Promise<JournalEntryT>) : null;
    }),
  authStatus: () => getJSON<AuthStatus>("/auth/status"),
  authLogin: (email: string, password: string) =>
    postJSON<LoginResponse>("/auth/login", { email, password }),
  authRefresh: () => postJSON<LoginResponse>("/auth/refresh"),
  authRegister: (email: string, password: string) =>
    postJSON<{ id: number; email: string }>("/auth/register", { email, password }),
  authMe: () => authedGet<{ id: number; email: string; created_at: string | null }>(
    "/auth/me",
  ),
  intelligence: (source: "live" | "backtest" = "live", jobId?: number) => {
    const q = new URLSearchParams({ source });
    if (source === "backtest" && jobId != null) q.set("job_id", String(jobId));
    return getJSON<IntelligencePayload>(`/intelligence?${q.toString()}`);
  },
  expectancyCurve: (
    source: "live" | "backtest" = "live",
    jobId?: number,
    window = 20,
  ) => {
    const q = new URLSearchParams({ source, window: String(window) });
    if (source === "backtest" && jobId != null) q.set("job_id", String(jobId));
    return getJSON<ExpectancyCurve>(`/intelligence/expectancy-curve?${q.toString()}`);
  },
  marketDataCoverage: () =>
    getJSON<{ series: MarketDataSeries[] }>("/market-data/coverage"),
  marketDataGaps: (symbol: string, timeframe: string) =>
    getJSON<MarketDataGaps>(
      `/market-data/gaps?${new URLSearchParams({ symbol, timeframe }).toString()}`,
    ),
  marketDataFill: (body: { symbol: string; timeframe: string; start: string; end: string }) =>
    postJSON<MarketDataFill>("/market-data/fill", body),
  marketDataFills: () => getJSON<{ fills: MarketDataFill[] }>("/market-data/fills"),
  marketDataQuality: (symbol: string, timeframe: string) =>
    getJSON<MarketDataQuality>(
      `/market-data/quality?${new URLSearchParams({ symbol, timeframe }).toString()}`,
    ),
  marketDataClean: (symbol: string, timeframe: string) =>
    postJSON<{ excluded: number; by_reason: Record<string, number> }>(
      "/market-data/clean", { symbol, timeframe },
    ),
  marketDataRestore: (symbol: string, timeframe: string) =>
    postJSON<{ restored: number }>("/market-data/restore", { symbol, timeframe }),
  marketDataImport: async (file: File, symbol: string, timeframe: string) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("symbol", symbol);
    fd.append("timeframe", timeframe);
    try {
      const r = await fetch("/market-data/import", {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      const body = await r.json().catch(() => null);
      if (!r.ok) {
        maybeNotify401(r.status);
        return { error: String(body?.detail ?? `HTTP ${r.status}`), status: r.status };
      }
      return body as MarketDataImportResult;
    } catch (err) {
      return { error: String(err), status: 0 };
    }
  },
  backtestStrategies: () =>
    getJSON<{ strategies: BacktestStrategySpec[] }>("/backtest/strategies"),
  backtestResults: () =>
    getJSON<{ jobs: BacktestJobSummary[] }>("/backtest/results"),
  backtestJob: (id: number) => getJSON<BacktestJobDetail>(`/backtest/${id}`),
  backtestRun: async (file: File | null, config: BacktestRunConfig & WithStore) => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("config", JSON.stringify(config));
    fd.append("sync", "false");
    try {
      const r = await fetch("/backtest/run", {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!r.ok) {
        maybeNotify401(r.status);
        let detail = `HTTP ${r.status}`;
        try {
          const body = await r.json();
          if (body?.detail) detail = body.detail;
        } catch {
          // ignore
        }
        return { error: detail, status: r.status };
      }
      return (await r.json()) as { job_id: number; status: string };
    } catch (err) {
      return { error: String(err), status: 0 };
    }
  },
  backtestSweep: async (file: File | null, config: BacktestSweepConfig & WithStore) => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("config", JSON.stringify(config));
    try {
      const r = await fetch("/backtest/sweep", {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!r.ok) {
        maybeNotify401(r.status);
        let detail = `HTTP ${r.status}`;
        try {
          const body = await r.json();
          if (body?.detail) detail = body.detail;
        } catch {
          // ignore
        }
        return { error: detail, status: r.status };
      }
      return (await r.json()) as BacktestSweepResult;
    } catch (err) {
      return { error: String(err), status: 0 };
    }
  },
  // Fetch MT5 history as a backtest-ready CSV File (L14 slice 5). Returns
  // a File the run/sweep/walk-forward forms can use as their upload.
  backtestMt5Bars: async (symbol: string, timeframe: string, count: number) => {
    const q = new URLSearchParams({ symbol, timeframe, count: String(count) });
    try {
      const r = await fetch(`/backtest/mt5-bars?${q.toString()}`, {
        headers: authHeaders(),
      });
      if (!r.ok) {
        maybeNotify401(r.status);
        let detail = `HTTP ${r.status}`;
        try {
          const body = await r.json();
          if (body?.detail) detail = body.detail;
        } catch {
          // ignore
        }
        return { error: detail, status: r.status };
      }
      const text = await r.text();
      return new File([text], `mt5-${symbol}-${timeframe}.csv`, { type: "text/csv" });
    } catch (err) {
      return { error: String(err), status: 0 };
    }
  },
  backtestWalkForward: async (file: File | null, config: WalkForwardConfig & WithStore) => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("config", JSON.stringify(config));
    try {
      const r = await fetch("/backtest/walk-forward", {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!r.ok) {
        maybeNotify401(r.status);
        let detail = `HTTP ${r.status}`;
        try {
          const body = await r.json();
          if (body?.detail) detail = body.detail;
        } catch {
          // ignore
        }
        return { error: detail, status: r.status };
      }
      return (await r.json()) as WalkForwardResult;
    } catch (err) {
      return { error: String(err), status: 0 };
    }
  },
  backtestValidateBasket: (config: BasketValidationConfig) =>
    postJSON<ValidationResult>("/backtest/validate-basket", config),
  backtestValidate: async (file: File | null, config: ValidationConfig & WithStore) => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("config", JSON.stringify(config));
    try {
      const r = await fetch("/backtest/validate", {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!r.ok) {
        maybeNotify401(r.status);
        let detail = `HTTP ${r.status}`;
        try {
          const body = await r.json();
          if (body?.detail) detail = body.detail;
        } catch {
          // ignore
        }
        return { error: detail, status: r.status };
      }
      return (await r.json()) as ValidationResult;
    } catch (err) {
      return { error: String(err), status: 0 };
    }
  },
  journalCsvUrl: (params: { symbol?: string; tag?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.symbol) q.set("symbol", params.symbol);
    if (params.tag) q.set("tag", params.tag);
    if (params.search) q.set("search", params.search);
    const qs = q.toString();
    return `/journal/export.csv${qs ? `?${qs}` : ""}`;
  },
  listSettings: () =>
    getJSON<{ settings: RuntimeSetting[] }>("/settings"),
  setSetting: (key: string, value: boolean) =>
    putJSON<{ ok: true; settings: RuntimeSetting[] }>(
      `/settings/${encodeURIComponent(key)}`,
      { value },
    ),
  listTradingConfig: () =>
    getJSON<{ config: TradingConfigEntry[] }>("/trading-config"),
  // What the enabled strategies imply for the live config, and a
  // validated-then-applied batch write for acting on it.
  configProposal: () => getJSON<ConfigProposalResponse>("/trading-config/proposal"),
  setTradingConfigBulk: (values: Record<string, number | string | string[]>) =>
    postJSON<{
      ok: true;
      applied: Record<string, number | string | string[]>;
      config: TradingConfigEntry[];
    }>("/trading-config/bulk", { values }),
  setTradingConfig: (key: string, value: number | string | string[]) =>
    putJSON<{
      ok: true;
      key: string;
      value: number | string | string[];
      config: TradingConfigEntry[];
    }>(`/trading-config/${encodeURIComponent(key)}`, { value }),
  // Sensitive credentials (Tier 2). GET returns only metadata + booleans —
  // the plaintext never crosses the API.
  listSecrets: () =>
    getJSON<{ secrets: SecretEntry[]; encryption_configured: boolean }>(
      "/secrets",
    ),
  setSecret: (key: string, value: string) =>
    putJSON<{ ok: true; key: string; secrets: SecretEntry[] }>(
      `/secrets/${encodeURIComponent(key)}`,
      { value },
    ),
  getMt5: () => getJSON<{ mt5: Mt5Status }>("/mt5/credentials"),
  setMt5: (login: number, password: string, server: string) =>
    putJSON<SetMt5Result>("/mt5/credentials", { login, password, server }),
  // TradingView setup page — recent inbound signals, secret reveal,
  // synthetic self-test webhook.
  tradingviewSetup: () =>
    getJSON<{
      recent_signals: TradingViewRecentSignal[];
      secret_configured: boolean;
      secret_is_user_set: boolean;
    }>("/tradingview/setup"),
  tradingviewSecret: () =>
    getJSON<{ value: string; is_user_set: boolean }>("/tradingview/secret"),
  tradingviewRotateSecret: () =>
    postJSON<{ value: string; is_user_set: boolean }>("/tradingview/secret/rotate"),
  // Bridge pairing (Trading-account page). The pairing code is the only
  // thing that ever leaves the backend unauthenticated — via /bridge/pair,
  // which the bridge process calls once.
  bridgePairing: () => getJSON<BridgePairingState>("/bridge/pairing"),
  bridgeCreatePairing: () =>
    postJSON<BridgePairingState & { code: string; expires_in: number }>("/bridge/pairing"),
  bridgeCancelPairing: () => deleteJSON<BridgePairingState>("/bridge/pairing"),
  bridgeToken: () => getJSON<{ value: string; is_user_set: boolean }>("/bridge/token"),
  bridgeRotateToken: () => postJSON<BridgePairingState & { ok: true }>("/bridge/token/rotate"),
  tradingviewSelfTest: (
    symbol: string,
    action: "buy" | "sell" | "close" | "veto",
    price: number,
    strategy_id?: string,
  ) =>
    postJSON<{
      ok: boolean;
      status_code?: number;
      response?: unknown;
      error?: string;
      payload_sent: Record<string, unknown>;
      /** How far the test signal actually travelled. In strict convergence
       * mode it stops at "convergence" and never exercises execution, so
       * `ok: true` alone does not mean the full chain was tested. */
      reached?: "webhook" | "convergence" | "executor" | "unknown";
      warning?: string;
      convergence_mode?: "single" | "strict" | "boost";
      enabled_strategies?: string[];
    }>("/tradingview/test", {
      symbol,
      action,
      price,
      ...(strategy_id ? { strategy_id } : {}),
    }),
  clearMt5: async (): Promise<
    | { ok: true; cleared: number; mt5: Mt5Status }
    | { error: string; status: number }
  > => {
    try {
      const r = await fetch("/mt5/credentials", {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!r.ok) {
        maybeNotify401(r.status);
        let detail = `HTTP ${r.status}`;
        try {
          const b = await r.json();
          if (b?.detail) detail = b.detail;
        } catch {
          // ignore
        }
        return { error: detail, status: r.status };
      }
      return (await r.json()) as {
        ok: true;
        cleared: number;
        mt5: Mt5Status;
      };
    } catch (err) {
      return { error: String(err), status: 0 };
    }
  },
  clearSecret: async (
    key: string,
  ): Promise<
    | { ok: true; key: string; deleted: boolean; secrets: SecretEntry[] }
    | { error: string; status: number }
  > => {
    try {
      const r = await fetch(`/secrets/${encodeURIComponent(key)}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!r.ok) {
        maybeNotify401(r.status);
        let detail = `HTTP ${r.status}`;
        try {
          const b = await r.json();
          if (b?.detail) detail = b.detail;
        } catch {
          // ignore
        }
        return { error: detail, status: r.status };
      }
      return (await r.json()) as {
        ok: true;
        key: string;
        deleted: boolean;
        secrets: SecretEntry[];
      };
    } catch (err) {
      return { error: String(err), status: 0 };
    }
  },
};

export interface RuntimeSetting {
  key: string;
  label: string;
  description: string;
  value: boolean;
  is_override: boolean;
  default: boolean;
}

/** The typed trading knobs (risk/sizing/convergence/news/notifications)
 *  exposed by the /trading-config endpoint. `value_type` drives the
 *  input control (numeric field for int/float, comma-text for
 *  string_list). `group` is used to bucket entries on the UI page. */
/** One row from the /tradingview/setup `recent_signals` list. Mirrors the
 *  shape /tradingview/setup serializes for the verification table. */
export interface TradingViewRecentSignal {
  id: number;
  correlation_id: string;
  strategy_id: string;
  symbol: string;
  action: string;
  price: number | null;
  signal_timestamp: string;
  source_ip: string | null;
  received_at: string | null;
}

/** Snapshot of what the backend currently has for MT5 + the bridge's
 *  view of the live connection. Drives the Trading-account page. */
export interface Mt5Status {
  login: number | null;
  server: string | null;
  has_password: boolean;
  is_user_set: boolean;
  bridge_connected: boolean;
  bridge_login: number | null;
  bridge_server: string | null;
  has_env_fallback: boolean;
}

/** GET /bridge/pairing — is a token configured, is a code live. Never
 *  carries the code or the token. */
export interface BridgePairingState {
  token_configured: boolean;
  token_is_user_set: boolean;
  pairing_active: boolean;
  pairing_expires_in: number | null;
}

/** Response from PUT /mt5/credentials — partial-success shape so the
 *  UI can distinguish "saved + pushed + connected" from "saved but
 *  bridge offline / bridge rejected creds". */
export interface SetMt5Result {
  saved: true;
  pushed: boolean;
  account: Record<string, unknown> | null;
  bridge_error: string | null;
  mt5: Mt5Status;
}

/** A sensitive credential row returned by /secrets. Never carries the
 *  plaintext — only the metadata + booleans the UI needs to decide what
 *  to show (set / clear / "configured via env"). */
export interface SecretEntry {
  key: string;
  label: string;
  description: string;
  group: string;
  placeholder: string;
  is_user_set: boolean;
  has_env_fallback: boolean;
  has_any_value: boolean;
}

/** Recommended convergence window for the enabled set: bars(n) x the
 *  longest timeframe any selected strategy was validated on. */
export interface WindowRecommendation {
  window_seconds: number;
  bars: number;
  timeframe: string;
  from_strategy: string;
  basis: string;
}

/** GET /signals/local - what the local runner would do right now. The
 *  preview evaluates without submitting, so it is safe to poll. */
export interface LocalSignalResult {
  strategy: string;
  signal: {
    strategy_id: string;
    symbol: string;
    action: string;
    price: number;
    timestamp: string;
    timeframe?: string;
    sl?: number;
    tp?: number;
  } | null;
  submitted?: { ok: boolean; status_code?: number; error?: string };
  /** Why it could not be evaluated (no validated run, bridge offline...). */
  note?: string | null;
  /** RUN NOW: what became of the submitted signal, in words. */
  outcome?: string;
}

export interface LocalSignalSubmission {
  at: string;
  strategy: string;
  action: string;
  symbol: string;
  bar: string;
  outcome: string;
}

export interface LocalSignalStatus {
  enabled: boolean;
  strategies: string[];
  interval_seconds: number;
  preview: LocalSignalResult[];
  last_run?: { at: string | null; error: string | null; evaluated: number; fired: number };
  recent?: LocalSignalSubmission[];
}

/** One tradeable instrument as the broker names it. `currency_base` is
 *  empty for anything that is not an FX pair (metals, indices, crypto). */
export interface BrokerSymbol {
  name: string;
  description: string;
  visible: boolean;
  digits: number;
  currency_base: string;
  currency_profit: string;
}

/** An instrument's contract spec. `source` is "broker" when it came from
 *  MT5 via the bridge and "fallback" when the bridge could not be asked
 *  and the operator's sizing.* defaults were used instead. */
export interface InstrumentSpec {
  symbol: string;
  broker_symbol: string | null;
  pip_size: number;
  pip_value_per_lot: number;
  volume_min: number;
  volume_step: number;
  volume_max: number;
  digits: number;
  stops_level_points: number;
  min_stop_distance: number;
  source: "broker" | "fallback";
}

/** A strategy the operator backtested and chose to keep (Backtest >
 *  Favourites). `validated` is the metrics of the run it was saved from. */
export interface FavouriteStrategy {
  id: number;
  strategy_id: string;
  strategy_name: string;
  params: Record<string, number>;
  note: string | null;
  job_id: number | null;
  created_at: string | null;
  validated: {
    symbol: string;
    timeframe: string;
    bars: number;
    completed_at: string | null;
    trades: number | null;
    win_rate: number | null;
    profit_factor: number | null;
    expectancy: number | null;
    total_pnl: number | null;
    max_drawdown_pct: number | null;
  } | null;
}

/** One row of GET /convergence/strategies: a strategy id and the evidence
 *  the system has for it (built-in, backtested, seen as live alerts). */
export interface StrategyCatalogueEntry {
  name: string;
  sources: ("favourite" | "builtin" | "backtest" | "signals")[];
  builtin_label: string | null;
  favourite: {
    id: number;
    strategy_name: string;
    note: string | null;
    job_id: number | null;
    symbol: string | null;
    timeframe: string | null;
    trades: number | null;
    profit_factor: number | null;
    expectancy: number | null;
  } | null;
  backtest: {
    runs: number;
    convergence_runs: number;
    best_profit_factor: number | null;
    best_job_id: number | null;
    latest: {
      job_id: number;
      completed_at: string | null;
      symbol: string;
      timeframe: string;
      bars: number;
      trades: number | null;
      profit_factor: number | null;
      expectancy: number | null;
      total_pnl: number | null;
    } | null;
  } | null;
  signals: { count: number; last_seen: string | null } | null;
  enabled: boolean;
}

/** One row of GET /trading-config/proposal: a config value the enabled
 *  strategies imply, with the run it was derived from. */
export interface ConfigProposal {
  key: string;
  label: string;
  unit: string;
  current: number | string | string[];
  proposed: number | string | string[];
  reason: string;
  source: string;
}

export interface ConfigProposalResponse {
  enabled: string[];
  proposals: ConfigProposal[];
  /** Enabled strategies tested with different values for the same key. */
  conflicts: {
    key: string;
    label: string;
    values: { strategy: string; value: number }[];
    using: string;
  }[];
  /** Enabled ids with no validated run behind them. */
  unvalidated: string[];
  sources: Record<
    string,
    { timeframe: string | null; symbol: string | null; origin: string; job_id: number | null }
  >;
}

export interface TradingConfigEntry {
  key: string;
  label: string;
  description: string;
  group: string;
  unit: string;
  value_type: "int" | "float" | "string" | "string_list";
  value: number | string | string[];
  is_override: boolean;
  default: number | string | string[];
  min: number | null;
  max: number | null;
  step: number | null;
  choices: string[] | null;
}
